<template>
  <CoreSelectionDrawer
    ref="selectionDrawerRef"
    has-search
    has-footer
    is-checked-variant
    title-truncate
    :value="isVisibleModal"
    :title="t('Тип клиники', options)"
    :placeholder="t('Тип клиники', options)"
    :items="lpuTypes"
    :selected-item="selectedLpuType"
    :error="isError"
    :loading="isLoading"
    :is-back="isBack"
    attach=".vue-the-lpu-filters .v-application"
    @click-item="onClickLpuType"
    @input="closeModal"
    @back="closeModal"
  >
    <template #append-header>
      <v-btn
        v-if="!isLpuTypeDefault"
        color="primary"
        small
        text
        class="ml-auto"
        @click="clickResetBtn"
      >
        {{ t('Сбросить', options) }}
      </v-btn>
    </template>
    <template #footer>
      <slot
        name="footer"
        v-bind="{ selectedLpuType }"
      >
        <div class="pa-2 elevation-8">
          <v-btn
            block
            depressed
            color="primary"
            :disabled="!filtersStore.hasUnsavedFilters"
            @click="$emit('lpu-type:selected', selectedLpuType)"
          >
            {{ t('Применить', options) }}
          </v-btn>
        </div>
      </slot>
    </template>
  </CoreSelectionDrawer>
</template>

<script setup lang="ts">
import {
  computed, watch, ref, toRefs,
} from 'vue'
import I18NextVue from 'i18next'
import apiLpuTypes from 'www/vue_components/common/TheFilters/lpu/api/apiLpuTypes'
import useCommonStore from 'www/vue_components/common/TheFilters/lpu/stores/useCommonStore'
import useFiltersStore from 'www/vue_components/common/TheFilters/lpu/stores/useFiltersStore'
import CoreSelectionDrawer from 'src/core/CoreSelectionDrawer/CoreSelectionDrawer.vue'
import type { Filters } from 'www/vue_components/common/TheFilters/lpu/stores/useFiltersStore.types'
import type { ApiLpuTypesParamsGet } from 'www/vue_components/common/TheFilters/lpu/api/apiLpuTypes.types'
import { DEFAULT_LPY_TYPE } from 'www/vue_components/common/TheFilters/lpu/components/LpuTypeFilters/constants'

const emit = defineEmits(['input', 'lpu-type:selected'])
const props = defineProps<{
  value: boolean
  isBack?: boolean
}>()

const { value: isVisibleModal } = toRefs(props)

const commonStore = useCommonStore()
const filtersStore = useFiltersStore()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const lpuTypes = ref<NonNullable<Filters['lpuType']>[]>([])
const lpuTypesCache: Map<string, NonNullable<Filters['lpuType']>[]> = new Map()
const selectedLpuType = computed(() => filtersStore.unsavedFilters.lpuType)
const isLpuTypeDefault = computed(() => !selectedLpuType.value)
const isError = ref(false)
const isLoading = ref(false)
const selectionDrawerRef = ref<InstanceType<typeof CoreSelectionDrawer>>()

const loadLpuTypes = async () => {
  isError.value = false
  isLoading.value = true

  try {
    const requestData: ApiLpuTypesParamsGet = {
      town_id: commonStore.town.id,
      is_town_lpu_view: !filtersStore.unsavedFilters.lpuType,
    }
    const cacheKey = JSON.stringify(requestData)

    if (lpuTypesCache.has(cacheKey)) {
      lpuTypes.value = lpuTypesCache.get(cacheKey) || []
      return
    }

    const { data } = await apiLpuTypes.get(requestData)

    lpuTypes.value = [{ ...DEFAULT_LPY_TYPE, name: t(DEFAULT_LPY_TYPE.name, options.value) }, ...data]

    lpuTypesCache.set(cacheKey, lpuTypes.value)
  } catch (e) {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}

watch(isVisibleModal, () => {
  if (isVisibleModal.value) {
    loadLpuTypes()
  }
})

const restartModal = () => {
  selectionDrawerRef.value?.resetSearch()
  selectionDrawerRef.value?.scrollToActiveItem()
}
const closeModal = () => {
  emit('input', false)
  restartModal()
}

const clickResetBtn = () => {
  filtersStore.updateUnsavedFilters({ lpuType: undefined })
  restartModal()
}

const isDefaultLpuType = (lpuType: Filters['lpuType']) => !lpuType || lpuType.translit === DEFAULT_LPY_TYPE.translit
const getValidLpuType = (lpuType: Filters['lpuType']) => (!isDefaultLpuType(lpuType) ? lpuType : undefined)
const onClickLpuType = (lpuType: Filters['lpuType']) => {
  filtersStore.updateUnsavedFilters({ lpuType: getValidLpuType(lpuType) })
}
</script>
